import React, { useEffect, useState } from "react";
import LifelineApi from "../../../services/api/Lifeline";
import styled from "styled-components";
import NoFooterModal from "../../Common/NoFooterModal";
import Button from "sushiweb/Button";
import Input from "sushiweb/InputTypes/Input";
import { round } from "lodash-es";
import variables from "../../../constants/assets/variables";
import Permission from "../../Common/Permission";
import MenuNames from "../../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../../constants/Menu/MenuActionsMappings";

const refundFormEmptyState = {
  amount: "",
  reason: "",
};

const PriceBreakup = ({ orderId, setStatus }) => {
  const [priceDetails, setPriceDetails] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [refundForm, setRefundForm] = useState(refundFormEmptyState);
  const allActions = MenuActionsMappings[MenuNames.LIFELINE];

  const getPriceDetails = async () => {
    try {
      const res = await LifelineApi.getLifeLineOrderPriceDetails(
        `order_id=${orderId}`
      );
      setPriceDetails(res);
    } catch (error) {
      setStatus({
        type: "error",
        message: "Error while retrieving price details",
      });
    }
  };

  const handleRefund = async () => {
    let totalAmount = priceDetails?.total_amount || 0;
    let refundAmount = priceDetails?.refund_amount || 0;
    let penaltyAmount = priceDetails?.penalty_amount || 0;
    if (totalAmount - refundAmount - penaltyAmount < refundForm.amount) {
      setStatus({
        type: "error",
        message: `Refund amount cannot be greater than ${round(
          totalAmount - refundAmount - penaltyAmount,
          2
        )}`,
      });
      return;
    } else if (refundForm.amount === "") {
      setStatus({
        type: "error",
        message: "Refund amount is mandatory",
      });
      return;
    } else if (refundForm.amount < 1) {
      setStatus({
        type: "error",
        message: "Refund amount must be >= 1",
      });
      return;
    } else if (refundForm.reason === "") {
      setStatus({
        type: "error",
        message: "Comments are mandatory",
      });
      return;
    }
    try {
      await LifelineApi.refundOrder({
        order_id: orderId,
        amount: parseFloat(refundForm.amount),
        reason: refundForm.reason,
      });
      setStatus({
        type: "success",
        message: "Refund initiated successfully",
      });
      getPriceDetails();
    } catch (error) {
      setStatus({
        type: "error",
        message: "Error while initiating refund",
      });
    }
    setRefundForm(refundFormEmptyState);
    handleClose();
  };

  const handleChange = (key, val) => {
    setRefundForm({ ...refundForm, [key]: val });
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const openModal = () => {
    setShowModal(true);
  };

  useEffect(() => {
    if (orderId) getPriceDetails();
  }, [orderId]);

  return (
    <PriceDetailsContainer>
      <PriceDetails>
        <InstructionHeading>Price Details:</InstructionHeading>
        {priceDetails?.total_amount && (
          <Instruction>
            <span>Total amount:</span> {priceDetails?.total_amount}
          </Instruction>
        )}
        {priceDetails?.penalty_amount && (
          <Instruction>
            <span>Penalty amount:</span> {priceDetails?.penalty_amount}
          </Instruction>
        )}
        {priceDetails?.payment_status && (
          <Instruction>
            <span>Payment status:</span> {priceDetails?.payment_status}
          </Instruction>
        )}
        {priceDetails?.payment_method && (
          <Instruction>
            <span>Payment method:</span> {priceDetails?.payment_method}
          </Instruction>
        )}
        {priceDetails?.refund_amount && (
          <Instruction>
            <span>Refund amount:</span> {priceDetails?.refund_amount}
          </Instruction>
        )}
      </PriceDetails>

      <ServiceFeeBreakupContainer>
        <ServiceHeading>Service Fee Breakup:</ServiceHeading>
        {priceDetails?.service_fee_breakup?.base_fee && (
          <Instruction>
            <span>Base Fee:</span> {priceDetails?.service_fee_breakup?.base_fee}
          </Instruction>
        )}
        {priceDetails?.service_fee_breakup?.additional_distance_fee && (
          <Instruction>
            <span>Aditional Distance Fee:</span>{" "}
            {priceDetails?.service_fee_breakup?.additional_distance_fee}
          </Instruction>
        )}
        {priceDetails?.service_fee_breakup?.additional_drop_charge && (
          <Instruction>
            <span>Additional Drop Charge:</span>{" "}
            {priceDetails?.service_fee_breakup?.additional_drop_charge}
          </Instruction>
        )}
        {priceDetails?.service_fee_breakup?.rain_surge && (
          <Instruction>
            <span>Rain Surge:</span>{" "}
            {priceDetails?.service_fee_breakup?.rain_surge}
          </Instruction>
        )}
        {priceDetails?.service_fee_breakup?.demand_surge && (
          <Instruction>
            <span>Demand Surge:</span>{" "}
            {priceDetails?.service_fee_breakup?.demand_surge}
          </Instruction>
        )}
        {priceDetails?.service_fee_breakup?.discount && (
          <Instruction>
            <span>Discount:</span> {priceDetails?.service_fee_breakup?.discount}
          </Instruction>
        )}
        {priceDetails?.service_fee_breakup?.gst_amount && (
          <Instruction>
            <span>GST amount:</span>{" "}
            {priceDetails?.service_fee_breakup?.gst_amount}
          </Instruction>
        )}
      </ServiceFeeBreakupContainer>

      <Permission requiredPerms={[allActions.LIFELINE_PAYMENTS]}>
        <RefundButton
          className="sushi-btn"
          onClick={openModal}
          disabled={!priceDetails?.allow_refund}
        >
          Initiate refund
        </RefundButton>
      </Permission>

      <>
        {showModal && (
          <NoFooterModal
            header={true}
            heading={`${"Order Action"}`}
            handleClose={handleClose}
            modalButtons={
              <Button
                data-testid="submitButton"
                className="sushi-btn"
                onClick={() => handleRefund()}
              >
                Submit
              </Button>
            }
          >
            <Input
              value={refundForm.amount}
              data-testid="refundAmount"
              id="sushi-input"
              label="Refund amount*"
              onChange={(val) => handleChange("amount", val)}
              type="number"
            />
            <Input
              value={refundForm.reason}
              data-testid="refundReason"
              id="sushi-input"
              onChange={(val) => handleChange("reason", val)}
              label="Comments*"
            />
          </NoFooterModal>
        )}
      </>
    </PriceDetailsContainer>
  );
};

export default PriceBreakup;

const PriceDetailsContainer = styled.div`
  display: flex;
  gap: 40px;
`;
const ServiceFeeBreakupContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ServiceHeading = styled.div`
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 10px;
  justify-content: space-between;
`;

const RefundButton = styled.button`
  margin: auto;
  padding: 10px 20px;
  background-color: ${variables.red500};
  color: white;
  cursor: pointer;
  border-radius: 5px;
  border: none;
  outline: none;

  &:hover {
    background-color: ${variables.red600};
  }

  &:disabled {
    background-color: ${variables.grey400};
    cursor: not-allowed;
  }
`;

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const Instruction = styled.div`
  display: flex;
  gap: 10px;
  max-width: 400px;
  margin-bottom: 4px;

  span {
    font-weight: bold;
    color: #828282;

    &:first-letter {
      text-transform: capitalize;
    }
  }
`;

const InstructionHeading = styled.div`
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
`;
